import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Instagram, Facebook, Youtube, Navigation } from "lucide-react";
import { INSTITUTE, BRANCHES } from "@/lib/content";

export default function Footer() {
  return (
    <footer className="bg-gray-950 text-gray-300 relative overflow-hidden" data-testid="main-footer">
      <div className="absolute top-0 left-0 right-0 h-1 bg-red-600" />

      {/* Footer CTA Strip */}
      <div className="border-b border-white/10 bg-gradient-to-r from-gray-950 via-gray-900 to-gray-950" data-testid="footer-cta-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="text-center lg:text-left max-w-2xl">
            <p className="font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-white leading-tight">
              "Reading is good, but <span className="text-red-500">guidance</span> is better. Join the <span className="text-red-500">500+ Test Series</span> and transform your scores."
            </p>
          </div>
          <Link
            to="/admissions"
            className="bg-red-600 text-white font-semibold px-7 py-4 hover:bg-red-700 transition-all duration-200 hover:shadow-[0_10px_30px_-10px_rgba(220,38,38,0.6)] hover:-translate-y-0.5 inline-flex items-center gap-2 text-sm whitespace-nowrap"
            data-testid="footer-counselling-cta"
          >
            Book a Free Counselling Session
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-4 md:grid-cols-2 gap-10">
        {/* Brand */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <img src={INSTITUTE.logo} alt="Kothari Classes" className="h-14 w-auto bg-white p-1" />
          </div>
          <p className="text-sm leading-relaxed text-gray-400 max-w-xs">
            Building strong foundations for a bright future. CBSE · ICSE · State Board coaching from Nursery to 12th with NEET · JEE · CET preparation.
          </p>
          <div className="flex gap-3 pt-2">
            {[
              { Icon: Instagram, href: INSTITUTE.socials.instagram, label: "instagram" },
              { Icon: Facebook, href: INSTITUTE.socials.facebook, label: "facebook" },
              { Icon: Youtube, href: INSTITUTE.socials.youtube, label: "youtube" },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 border border-gray-700 flex items-center justify-center hover:border-red-600 hover:text-red-500 transition-colors"
                data-testid={`footer-social-${label}`}
                aria-label={label}
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Quick links */}
        <div>
          <h4 className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase mb-5">Explore</h4>
          <ul className="space-y-3 text-sm">
            {[
              { to: "/", label: "Home" },
              { to: "/about", label: "About" },
              { to: "/courses", label: "Courses" },
              { to: "/admissions", label: "Admissions" },
              { to: "/blog", label: "Blog" },
              { to: "/resources", label: "Resources" },
              { to: "/testimonials", label: "Testimonials" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-white transition-colors" data-testid={`footer-link-${l.label.toLowerCase()}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Branches */}
        <div>
          <h4 className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase mb-5">Branches</h4>
          <ul className="space-y-5 text-sm">
            {BRANCHES.map((b) => (
              <li key={b.id} className="space-y-2">
                <div className="font-semibold text-white">{b.name}</div>
                <div className="flex gap-2 text-gray-400">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
                  <span>{b.address}</span>
                </div>
                <a
                  href={b.directions}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-directions-${b.id}`}
                  className="group inline-flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-white transition-colors"
                >
                  <Navigation className="w-3.5 h-3.5 transition-transform group-hover:rotate-12" />
                  Get Directions
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase mb-5">Get in touch</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3 items-center">
              <Phone className="w-4 h-4 text-red-500" />
              <a href={`tel:${INSTITUTE.phone_raw}`} className="hover:text-white">{INSTITUTE.phone}</a>
            </li>
            <li className="flex gap-3 items-center">
              <Mail className="w-4 h-4 text-red-500" />
              <a href={`mailto:${INSTITUTE.email}`} className="hover:text-white">{INSTITUTE.email}</a>
            </li>
          </ul>
          <Link to="/admissions" className="btn-primary mt-6 rounded-none text-sm" data-testid="footer-cta-demo">
            Book Free Demo →
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-gray-500">
          <span>© {new Date().getFullYear()} Kothari Classes. All rights reserved.</span>
          <span className="tracking-[0.2em] uppercase">Jalgaon · Maharashtra</span>
        </div>
      </div>
    </footer>
  );
}
